import React, { useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Group,
  Select,
  Text,
  Textarea,
  UnstyledButton,
} from "@mantine/core";
import { useClient } from "../../../../context/ClientContext";
import { useSalon } from "../../../../context/SalonContext";
import ClientLookup from "../../../common/ClientLookup";
import { DESIGN_ICONS } from "../../../../theme/designIcons";
import { formatPhoneNumber } from "../../../../utils/phoneUtils";
import { filterBookableActiveStaff } from "../../../../utils/staffUtils";
import type { Employee, SalonService } from "../../../../types/salon";
import {
  MAX_WALK_IN_COMMENTS_LENGTH,
  type WaitlistEntry,
} from "../../../../types/waitlist";
import { performableServiceIds } from "../booking/draftModel";
import { staffServiceFor } from "../booking/bookingTypes";
import { durationText, entryClientName } from "./waitlistModel";
import type { AssignChoice, AssignMode } from "./assignModel";
import { qualifiedFor, type StaffAvailability } from "./staffAvailability";
import styles from "./WalkInForm.module.css";

/** What the drawer / sheet shows: New walk-in, or Edit of a waiting row. */
export type WalkInFormTarget =
  | { mode: "new" }
  | { mode: "edit"; entry: WaitlistEntry };

export interface WalkInFormValues {
  clientUuid: string;
  clientName: string;
  serviceId: string;
  serviceName: string;
  /** Trimmed. */
  comments: string;
  /** New walk-in with a provider picked: book it right away. */
  assign?: { choice: AssignChoice; mode: AssignMode };
}

/** Availability for the Provider option (omitted = no Provider section). */
export interface WalkInFormAvailability {
  rows: StaffAvailability[];
  nowMs: number;
  nowWall: Date;
  timezone: string;
}

/** The form's parts, placed by the host (drawer NestedScreen / mobile screen). */
export interface WalkInFormParts {
  title: string;
  subtitle?: string;
  body: React.ReactNode;
  footer: React.ReactNode;
}

export interface WalkInFormProps {
  target: WalkInFormTarget;
  services: SalonService[];
  /** Resolves true when saved (the host closes the form). */
  onSave: (values: WalkInFormValues) => Promise<boolean>;
  onCancel: () => void;
  /** "sheet": mobile full screen (44px controls). */
  variant?: "drawer" | "sheet";
  frame: (parts: WalkInFormParts) => React.ReactElement;
  /** New walk-in only: the Provider (Waitlist / Assign provider) option. */
  availability?: WalkInFormAvailability;
}

const { Search, ListPlus, UserPlus, Shuffle } = DESIGN_ICONS;

/** Busy mode default: after their bookings when a gap fits, else now. */
const defaultMode = (who: StaffAvailability | undefined): AssignMode =>
  who && !who.available && who.freeAt ? "after" : "now";

const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("") || "—";

/**
 * New / Edit walk-in (design handoff §8 "Nested: New Walk-in"): Client (the
 * booking drawer's searchable ClientLookup incl. "+ Add a new client") →
 * Service (searchable) → Provider (new only: Waitlist, or Assign provider
 * with Any available / a qualified provider and the busy modes) → Customer
 * comments (optional, ≤ 500). Save needs a client and a service, plus a
 * provider when assigning.
 */
export default function WalkInForm({
  target,
  services,
  onSave,
  onCancel,
  variant = "drawer",
  frame,
  availability,
}: WalkInFormProps) {
  const edit = target.mode === "edit" ? target.entry : null;
  const { allClients } = useClient() as {
    allClients: {
      id: string;
      first_name?: string;
      last_name?: string;
      area_code?: string;
      phone_number?: string;
    }[];
  };
  const { employees } = useSalon() as { employees: Employee[] | undefined };

  const [clientUuid, setClientUuid] = useState(edit?.client_uuid ?? "");
  const [serviceId, setServiceId] = useState(edit?.salon_service_uuid ?? "");
  const [comments, setComments] = useState(edit?.comments ?? "");
  const [saving, setSaving] = useState(false);
  // Provider (new walk-in only): Waitlist (default) or Assign provider.
  const [assignOn, setAssignOn] = useState(false);
  const [choice, setChoice] = useState<AssignChoice | null>(null);
  const [mode, setMode] = useState<AssignMode>("after");

  const client = allClients.find((c) => c.id === clientUuid);
  const clientName = client
    ? `${client.first_name ?? ""} ${client.last_name ?? ""}`.trim()
    : edit && edit.client_uuid === clientUuid
      ? entryClientName(edit)
      : "";
  const clientPhone = client
    ? formatPhoneNumber(`${client.area_code ?? ""}${client.phone_number ?? ""}`)
    : "";

  // Services some bookable staff member performs (a walk-in nobody can take
  // is never offered); the edited row's service stays listed.
  const bookableIds = useMemo(
    () =>
      (filterBookableActiveStaff(employees || []) as Employee[]).map(
        (e) => e.id,
      ),
    [employees],
  );
  const showProvider = !edit && !!availability;
  const assigning = showProvider && assignOn;
  const rows = useMemo(() => availability?.rows ?? [], [availability]);
  const nowWall = availability?.nowWall;
  const serviceById = useMemo(
    () => new Map(services.map((s) => [s.id, s])),
    [services],
  );
  const service = serviceById.get(serviceId);
  // Providers: only those who perform the chosen service (prototype :501),
  // judged on whether it fits their calendar from now.
  const providers = useMemo(
    () => (service && nowWall ? qualifiedFor(rows, service, nowWall) : rows),
    [rows, service, nowWall],
  );
  const picked =
    assigning && choice && choice !== "any"
      ? providers.find((r) => r.id === choice) || null
      : null;
  // With a concrete provider picked, only what they perform (prototype).
  const serviceOptions = useMemo(() => {
    const allowed = performableServiceIds(services, bookableIds);
    return services
      .filter(
        (s) =>
          !allowed || allowed.has(s.id) || s.id === edit?.salon_service_uuid,
      )
      .filter((s) => !picked || !!staffServiceFor(s, picked.id))
      .map((s) => ({ value: s.id, label: s.name }));
  }, [services, bookableIds, edit?.salon_service_uuid, picked]);

  const anyAvailable = providers.some((r) => r.available);
  const providerOptions = [
    ...(anyAvailable ? [{ value: "any", label: "Any available" }] : []),
    ...providers.map((r) => ({ value: r.id, label: r.name })),
  ];
  const busy = !!picked && !picked.available;

  const pickService = (id: string) => {
    setServiceId(id);
    const svc = serviceById.get(id);
    // A provider who doesn't perform the new service is dropped.
    if (choice && choice !== "any" && svc && !staffServiceFor(svc, choice))
      setChoice(null);
    const fitted = svc && nowWall ? qualifiedFor(rows, svc, nowWall) : null;
    if (choice === "any" && fitted && !fitted.some((r) => r.available))
      setChoice(null);
    // The busy-mode default follows the provider's fit for the new service.
    if (choice && choice !== "any" && fitted)
      setMode(defaultMode(fitted.find((r) => r.id === choice)));
  };
  const pickProvider = (value: string | null) => {
    setChoice(value);
    setMode(
      defaultMode(
        value && value !== "any"
          ? providers.find((r) => r.id === value)
          : undefined,
      ),
    );
  };

  const invalid = !clientUuid || !serviceId || (assigning && !choice);

  const save = async () => {
    if (invalid || saving) return;
    setSaving(true);
    try {
      await onSave({
        clientUuid,
        clientName: clientName || "Client",
        serviceId,
        serviceName: service?.name ?? edit?.service_name ?? "",
        comments: comments.trim(),
        ...(assigning && choice
          ? { assign: { choice, mode: busy ? mode : "now" } }
          : {}),
      });
    } finally {
      setSaving(false);
    }
  };

  const saveLabel = edit
    ? "Save changes"
    : !assigning || !choice
      ? "Add to waitlist"
      : choice === "any"
        ? "Assign to any available"
        : `Assign to ${(picked?.name || "").split(" ")[0]}`;

  const sheet = variant === "sheet";
  const body = (
    <Box className={`${styles.form} ${sheet ? styles.sheet : ""}`}>
      <Text className={styles.label}>Client</Text>
      {clientUuid ? (
        <Box className={styles.clientCard}>
          <Avatar radius="xl" size={34} className={styles.clientAvatar}>
            {initialsOf(clientName)}
          </Avatar>
          <Box className={styles.clientBody}>
            <Text span className={styles.clientName}>
              {clientName || "Client"}
            </Text>
            {clientPhone && (
              <Text span className={styles.clientSub}>
                {clientPhone}
              </Text>
            )}
          </Box>
          <UnstyledButton
            className={styles.clientChange}
            onClick={() => setClientUuid("")}
            aria-label="Change client"
          >
            Change
          </UnstyledButton>
        </Box>
      ) : (
        <ClientLookup
          value={clientUuid}
          onChange={(v: string) => setClientUuid(v)}
          error={null}
          label={null}
          required={false}
          placeholder="Search by name or phone"
          pinCreate
          leftSection={<Search size={16} />}
          radius="md"
          size="md"
        />
      )}

      <Text className={`${styles.label} ${styles.spaced}`}>Service</Text>
      <Select
        size="md"
        placeholder="Select a service…"
        aria-label="Service"
        data={serviceOptions}
        value={serviceId || null}
        onChange={(v) => pickService(v ?? "")}
        searchable
        nothingFoundMessage="No matches"
        comboboxProps={{ withinPortal: sheet }}
        leftSection={
          service ? (
            <span
              className={styles.swatch}
              style={{
                background: service.default_calendar_event_color || "#8a8a8a",
              }}
            />
          ) : undefined
        }
        renderOption={({ option }) => {
          const s = serviceById.get(option.value);
          return (
            <Group gap={8} wrap="nowrap" w="100%">
              <span
                className={styles.swatch}
                style={{
                  background: s?.default_calendar_event_color || "#8a8a8a",
                }}
              />
              <Text size="sm" style={{ flex: 1, minWidth: 0 }} truncate>
                {option.label}
              </Text>
              <Text size="xs" c="dimmed">
                {durationText(s?.default_base_duration_mins ?? 0)}
              </Text>
            </Group>
          );
        }}
      />

      {showProvider && availability && (
        <>
          <Text className={`${styles.label} ${styles.spaced}`}>Provider</Text>
          <Box
            className={styles.segment}
            role="radiogroup"
            aria-label="Provider"
          >
            {[
              { on: false, label: "Waitlist", Icon: ListPlus },
              { on: true, label: "Assign provider", Icon: UserPlus },
            ].map(({ on, label, Icon }) => (
              <UnstyledButton
                key={label}
                role="radio"
                aria-checked={assignOn === on}
                data-checked={assignOn === on || undefined}
                className={styles.segmentBtn}
                onClick={() => {
                  setAssignOn(on);
                  if (on) pickProvider(choice ?? (anyAvailable ? "any" : null));
                  else setChoice(null);
                }}
              >
                <Icon size={15} />
                {label}
              </UnstyledButton>
            ))}
          </Box>
          {assigning && (
            <Select
              mt={10}
              size="md"
              aria-label="Provider"
              placeholder="Select a provider…"
              data={providerOptions}
              value={choice}
              onChange={pickProvider}
              searchable
              nothingFoundMessage="No matches"
              comboboxProps={{ withinPortal: sheet }}
              leftSection={choice === "any" ? <Shuffle size={15} /> : undefined}
              renderOption={({ option }) => {
                if (option.value === "any")
                  return (
                    <Group gap={8} wrap="nowrap">
                      <Shuffle size={15} />
                      <Text size="sm">Any available</Text>
                    </Group>
                  );
                const r = providers.find((p) => p.id === option.value);
                return (
                  <Group gap={8} wrap="nowrap" w="100%">
                    <Text size="sm" style={{ flex: 1, minWidth: 0 }} truncate>
                      {option.label}
                    </Text>
                    {r && (
                      <Text
                        size="xs"
                        className={
                          r.available ? styles.availableNote : styles.busyNote
                        }
                      >
                        {r.available ? "Available" : "Busy"}
                      </Text>
                    )}
                  </Group>
                );
              }}
            />
          )}
          {busy && picked && (
            <Box className={styles.busyCard}>
              <Text className={styles.busyTitle}>
                {picked.name} is busy · {picked.busyText}
              </Text>
              <Box role="radiogroup" className={styles.modes}>
                {[
                  ...(picked.freeAt
                    ? [{ key: "after" as const, label: picked.afterLabel }]
                    : []),
                  { key: "now" as const, label: "Start now" },
                ].map((m) => (
                  <UnstyledButton
                    key={m.key}
                    role="radio"
                    aria-checked={mode === m.key}
                    data-checked={mode === m.key || undefined}
                    className={styles.mode}
                    onClick={() => setMode(m.key)}
                  >
                    <span className={styles.radio} />
                    <span className={styles.modeLabel}>{m.label}</span>
                  </UnstyledButton>
                ))}
              </Box>
            </Box>
          )}
        </>
      )}

      <Text className={`${styles.label} ${styles.spaced}`}>
        Customer comments <span className={styles.optional}>(optional)</span>
      </Text>
      <Textarea
        aria-label="Customer comments"
        placeholder="Anything the client mentioned, e.g. “Has to leave by 3:30”"
        value={comments}
        onChange={(e) =>
          setComments(
            e.currentTarget.value.slice(0, MAX_WALK_IN_COMMENTS_LENGTH),
          )
        }
        maxLength={MAX_WALK_IN_COMMENTS_LENGTH}
        minRows={3}
        autosize
      />
      {comments.length >= MAX_WALK_IN_COMMENTS_LENGTH - 50 && (
        <Text size="xs" c="dimmed" ta="right" mt={4}>
          {comments.length}/{MAX_WALK_IN_COMMENTS_LENGTH}
        </Text>
      )}
    </Box>
  );

  const footer = (
    <Group gap={8} justify="flex-end" w="100%">
      <Button variant="default" size="md" onClick={onCancel}>
        Cancel
      </Button>
      <Button
        size="md"
        onClick={() => void save()}
        disabled={invalid}
        loading={saving}
      >
        {saveLabel}
      </Button>
    </Group>
  );

  return frame({
    title: edit ? "Edit walk-in" : "New walk-in",
    subtitle: edit ? "Update client, service or comments" : undefined,
    body,
    footer,
  });
}
